import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Modal } from "./Modal";

// ─── Command registry ─────────────────────────────────────────────────────
// Each command needs: id, label, group, action(navigate, extras), optional kbd
//
// `hasFeature` gates the entries that belong to a per-user module: RBTE 403s
// on every route for an account without the `rbte` flag, so offering the
// command would only route them to an empty state.

function buildCommands(navigate, onToggleTheme, hasFeature) {
  return [
    { id: "home",     label: "Go to Home",        group: "Navigate", kbd: "H", action: () => navigate("/") },
    { id: "score",    label: "Score an event",     group: "Navigate", kbd: "S", action: () => navigate("/score") },
    { id: "practice", label: "Log practice",       group: "Navigate", kbd: "P", action: () => navigate("/practice") },
    { id: "round",    label: "View last round",    group: "Navigate", kbd: "R", action: () => navigate("/round") },
    ...(hasFeature("rbte")
      ? [{ id: "rbte", label: "Read Between The Ends", group: "Navigate",       action: () => navigate("/rbte") }]
      : []),
    { id: "theme",    label: "Toggle theme",       group: "Settings",           action: onToggleTheme },
  ];
}

// ─── CommandPalette ───────────────────────────────────────────────────────
export function CommandPalette({ onClose, onToggleTheme }) {
  const navigate   = useNavigate();
  const { hasFeature } = useAuth();
  const inputRef   = useRef(null);
  const [query, setQuery]     = useState("");
  const [active, setActive]   = useState(0);

  const commands = buildCommands(navigate, onToggleTheme, hasFeature);
  const filtered = query
    ? commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : commands;

  // focus input on open
  useEffect(() => { inputRef.current?.focus(); }, []);

  // reset active index when filter changes
  useEffect(() => { setActive(0); }, [query]);

  function run(cmd) {
    cmd.action();
    onClose();
  }

  // Escape is owned by <Modal> (document-level), so it is not handled here.
  function handleKeyDown(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); return; }
    if (e.key === "Enter" && filtered[active]) { run(filtered[active]); return; }
  }

  return (
    <Modal variant="palette" onClose={onClose} label="Command palette">
      <div className="palette-input-row">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="palette-search-icon">
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref={inputRef}
          className="palette-input"
          placeholder="Search commands…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
        />
        <kbd className="palette-esc" onClick={onClose}>ESC</kbd>
      </div>

      <div className="palette-results">
        {filtered.length === 0 && (
          <div className="palette-empty">No commands match "{query}"</div>
        )}
        {filtered.map((cmd, i) => (
          <button
            key={cmd.id}
            className={"palette-item" + (i === active ? " active" : "")}
            onMouseEnter={() => setActive(i)}
            onClick={() => run(cmd)}
          >
            <span className="palette-item-label">{cmd.label}</span>
            <span className="palette-item-group">{cmd.group}</span>
            {cmd.kbd && <kbd className="palette-item-kbd">{cmd.kbd}</kbd>}
          </button>
        ))}
      </div>

      <div className="palette-footer">
        <span><kbd>↑↓</kbd> navigate</span>
        <span><kbd>↵</kbd> run</span>
        <span><kbd>⌘K</kbd> close</span>
      </div>
    </Modal>
  );
}
